// hospitalSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IHospital,
  IHospitalInitialState,
  IHospitalRating,
  IHospitalImage,
} from "./hospitalModal";
import {
  activeInactiveHospital,
  createHospital,
  deleteHospital,
  deleteHospitalImage,
  deleteHospitalRating,
  getAllActiveHospitals,
  getAllHospitals,
  getHospitalByID,
  getHospitalImagesByID,
  getHospitalRatingsByID,
  insertHospitalRating,
  updateHospital,
  updateHospitalRating,
  uploadHospitalMultipleImages,
  uploadHospitalSingleImage,
} from "./services/hospital.service";

const initialState: IHospitalInitialState = {
  loading: false,
  formLoading: false,
  MultiImageformLoading: false,
  error: "",
  HospitalID: null,
  HospitalList: [],
  HospitalRatings: [],
  HospitalImages: [],
  totalCount: 0,
  HospitalDetail: {
    ID: null,
    HospitalName: "",
    HospitalTypeID: null,
    Accreditations: "",
    RegistrationNo: "",
    Url: "",
    TagLine: "",
    Address: "",
    Area: "",
    CityID: 0,
    StateID: 0,
    MobileNo: "",
    EmailID: "",
    Website: "",
    MapURL: "",
    MainImage: "",
    RegistrationCertificateImage: "",
    AccreditationCertificateImage: "",
    EstablishedYear: 0,
    AboutHospital: "",
    TotalBed: 0,
    TotalDoctors: 0,
    OPDTime: "",
    IsEmergencyAvailable: false,
    AvailableSevice: [],
    Specialties: [],
    Facilities: [],
    IsActive: 1,
    Ratings: [],
    Images: [],
    AverageRating: 0,
  },
  FilterHospital: {
    HospitalName: "",
    Area: "",
    CityName: "",
    StateName: "",
    TotalBed: null,
    TotalDoctors: null,
    Skip: 0,
    Limit: 10,
  },
};

const hospitalSlice = createSlice({
  name: "hospital",
  initialState,
  reducers: {
    clearHospitalDetails: (state) => {
      state.HospitalDetail = initialState.HospitalDetail;
    },
    setHospitalID: (state, action) => {
      state.HospitalID = action.payload;
    },
    clearHospitalID: (state) => {
      state.HospitalID = initialState.HospitalID;
    },
    clearHospitalRatings: (state) => {
      state.HospitalRatings = [];
    },
    clearHospitalImages: (state) => {
      state.HospitalImages = [];
    },
    setHospitalList: (state, action) => {
      state.HospitalList = action.payload;
    },
    setFilterHospital: (state, action) => {
      state.FilterHospital = action.payload;
    },
    clearFilterHospital: (state) => {
      state.FilterHospital = initialState.FilterHospital;
    },
  },
  extraReducers: (builder) => {
    // Get All Hospitals
    builder.addCase(getAllHospitals.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllHospitals.fulfilled,
      (
        state,
        action: PayloadAction<{ Data: IHospital[]; TotalCount: number }>,
      ) => {
        state.loading = false;
        state.HospitalList = action.payload.Data || [];
      },
    );
    builder.addCase(getAllHospitals.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.HospitalList = [];
    });

    // Create Hospital
    builder.addCase(createHospital.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createHospital.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(createHospital.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Update Hospital
    builder.addCase(updateHospital.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updateHospital.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateHospital.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Delete Hospital
    builder.addCase(deleteHospital.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteHospital.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteHospital.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Get Hospital By ID
    builder.addCase(getHospitalByID.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(getHospitalByID.fulfilled, (state, action: any) => {
      state.formLoading = false;
      state.HospitalDetail = action.payload;
    });
    builder.addCase(getHospitalByID.rejected, (state, action) => {
      state.formLoading = false;
      state.HospitalDetail = initialState.HospitalDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // Get All Active Hospitals
    builder.addCase(getAllActiveHospitals.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveHospitals.fulfilled, (state, action) => {
      state.loading = false;
      state.HospitalList = action.payload;
    });
    builder.addCase(getAllActiveHospitals.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Active/Inactive Hospital
    builder.addCase(activeInactiveHospital.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(activeInactiveHospital.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInactiveHospital.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Hospital Ratings
    builder.addCase(insertHospitalRating.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertHospitalRating.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertHospitalRating.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateHospitalRating.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateHospitalRating.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateHospitalRating.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteHospitalRating.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteHospitalRating.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteHospitalRating.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getHospitalRatingsByID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getHospitalRatingsByID.fulfilled,
      (state, action: PayloadAction<IHospitalRating[]>) => {
        state.loading = false;
        state.HospitalRatings = action.payload || [];
      },
    );
    builder.addCase(getHospitalRatingsByID.rejected, (state, action) => {
      state.loading = false;
      state.HospitalRatings = [];
      state.error = action.error.message || "Something went wrong";
    });

    // Hospital Images
    builder.addCase(uploadHospitalSingleImage.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(uploadHospitalSingleImage.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(uploadHospitalSingleImage.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(uploadHospitalMultipleImages.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(uploadHospitalMultipleImages.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(uploadHospitalMultipleImages.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteHospitalImage.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteHospitalImage.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteHospitalImage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getHospitalImagesByID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getHospitalImagesByID.fulfilled,
      (state, action: PayloadAction<IHospitalImage[]>) => {
        state.loading = false;
        state.HospitalImages = action.payload || [];
      },
    );
    builder.addCase(getHospitalImagesByID.rejected, (state, action) => {
      state.loading = false;
      state.HospitalImages = [];
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearHospitalDetails,
  clearHospitalID,
  setHospitalID,
  clearHospitalRatings,
  clearHospitalImages,
  setHospitalList,
  setFilterHospital,
  clearFilterHospital,
} = hospitalSlice.actions;

export default hospitalSlice.reducer;
